<template>
  <b-row v-if="kycData" class="kycInfoWrap">
    <b-col lg="4" class="kycInfo my-3">
      <div
        v-if="kycData.provider_applicant_id !== undefined"
        class="provider_applicant_id"
        @click="copyToCb($refs.provider_applicant_id, kycData.provider_applicant_id)"
      >
        <span class="greyText">Provider application ID:</span>
        <span ref="provider_applicant_id">{{ kycData.provider_applicant_id }}</span>
      </div>
      <div
        v-if="kycData.provider_level_name !== undefined"
        @click="copyToCb($refs.provider_level_name, kycData.provider_level_name)"
      >
        <span class="greyText">Provider level name:</span>
        <span ref="provider_level_name">{{ kycData.provider_level_name }}</span>
      </div>
      <div v-if="kycData.provider_status !== undefined">
        <span class="greyText">Provider status:</span>
        <span>{{ kycData.provider_status }}</span>
      </div>
      <div v-if="kycData.enforcement_status !== undefined">
        <span class="greyText">Enforcement status:</span>
        <span>{{ kycData.enforcement_status }}</span>
      </div>
      <div v-if="kycData.processing_status !== undefined">
        <span class="greyText">Processing status:</span>
        <span>{{ kycData.processing_status }}</span>
      </div>
      <div v-if="kycData.trigger_reason !== undefined">
        <span class="greyText">Trigger reason:</span>
        <span>{{ kycData.trigger_reason }}</span>
      </div>
      <div v-if="kycData.notification_percent !== undefined">
        <span class="greyText">Notification percent:</span>
        <span>{{ kycData.notification_percent }}</span>
      </div>
      <div v-if="kycData.notification_timestamp !== undefined">
        <span class="greyText">Notification date:</span>
        <span>{{ format(new Date(kycData.notification_timestamp * 1000), 'yyyy-MM-dd HH:mm:ss') }}</span>
      </div>
      <div v-if="kycData.finalized_at !== undefined" class="finalized_at">
        <span class="greyText">Finalized at date:</span>
        <span>{{
          kycData.finalized_at !== null ? format(new Date(kycData.finalized_at * 1000), 'yyyy-MM-dd HH:mm:ss') : null
        }}</span>
      </div>
      <div v-if="kycData.can_retry !== undefined">
        <span class="greyText">Can retry:</span>
        <span>{{ kycData.can_retry }}</span>
      </div>
      <div class="mb-2 limitsHeader">
        <span>Using limits:</span>
      </div>
      <div v-if="kycData.limits?.activation_api?.current_percent !== undefined">
        <span class="greyText">Activation api:</span>
        <span>{{ kycData.limits?.activation_api?.current_percent }}%</span>
      </div>
      <div v-if="kycData.limits?.activation_retail?.current_percent !== undefined">
        <span class="greyText">Retail api:</span>
        <span>{{ kycData.limits?.activation_retail?.current_percent }}%</span>
      </div>
      <div v-if="kycData.limits?.activation_whitelabel?.current_percent !== undefined">
        <span class="greyText">Whitelabel api:</span>
        <span>{{ kycData.limits?.activation_whitelabel?.current_percent }}%</span>
      </div>
    </b-col>
    <b-col lg="3" class="my-3">
      <b-row>
        <b-col xs="6" class="px-xl-2">
          <b-button
            @click="changeKycModal = true"
            :disabled="loading"
            class="accountActionBtn mb-3 mb-xl-5"
            variant="outline-primary"
          >
            <b-spinner v-if="loading" small variant="danger" />
            <span v-else>Change KYC status</span>
          </b-button>
        </b-col>
      </b-row>
    </b-col>
  </b-row>

  <b-modal
    v-model="changeKycModal"
    @keydown.enter="changeKycStatus"
    title="Change KYC status"
    class="questionModal"
    centered
    hide-footer
  >
    <div>
      <span class="greyText">Enforcement status:</span>
      <span>
        <select v-model="enforcementStatus" class="mb-4">
          <option value="pending">Pending</option>
          <option value="required">Required</option>
          <option value="finished">Finished</option>
          <option value="skipped">Skipped</option>
        </select>
      </span>
    </div>

    <div>
      <span class="greyText">Trigger reason:</span>
      <span>
        <select v-model="triggerReason" class="mb-4">
          <option value="none">None</option>
          <option value="api">Api</option>
          <option value="retail">Retail</option>
          <option value="whitelabel">Whitelabel</option>
          <option value="manual">Manual</option>
        </select>
      </span>
    </div>

    <div class="btnBl">
      <b-button @click="changeKycModal = false" variant="light">Cancel</b-button>
      <b-button @click="changeKycStatus" variant="success" class="confirmBtn">Save</b-button>
    </div>
  </b-modal>
</template>

<script>
import { format } from 'date-fns'
import { useSearch } from '@/use/search'
import { useActAdminApi } from '@/use/actAdminApi'
import { useErrorHandler } from '@/use/errorHandler'
import { useCopy } from '@/use/copy'

export default {
  props: {
    account: Object
  },
  data() {
    return {
      format,
      changeKycModal: false,
      enforcementStatus: '',
      triggerReason: ''
    }
  },
  computed: {
    loading() {
      return this.$store.state.loaders.commonLoading
    },
    kycData() {
      return this.$store.state.search.kycData
    }
  },
  watch: {
    kycData(val) {
      if (val) {
        this.enforcementStatus = val.enforcement_status || ''
        this.triggerReason = val.trigger_reason || ''
      }
    }
  },
  setup() {
    const { getSearchData } = useSearch()
    const { initApiInst, actUserApi } = useActAdminApi()
    const { handleCommonErrors } = useErrorHandler()
    const { copyToCb } = useCopy()
    return { getSearchData, initApiInst, actUserApi, handleCommonErrors, copyToCb }
  },
  methods: {
    closeModal() {
      this.changeKycModal = false
    },
    async changeKycStatus() {
      try {
        this.closeModal()
        this.$store.commit('loaders/setCommonLoading', true)
        await this.initApiInst()
        await this.actUserApi.updateKyc(this.account.id, {
          updateKycRequest: {
            enforcement_status: this.enforcementStatus,
            trigger_reason: this.triggerReason
          }
        })
        await this.getSearchData(this.account.fbuid)
        this.$store.commit('alert/add', {
          id: Date.now(),
          text: 'KYC status successfully updated',
          timeout: 3000,
          type: 'success',
          name: 'kyc_status_updated'
        })
      } catch (err) {
        this.handleCommonErrors(err)
      } finally {
        this.$store.commit('loaders/setCommonLoading', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.kycInfoWrap {
  margin-top: 60px;
  @media screen and (max-width: 767px) {
    margin-top: 40px;
  }

  .kycInfo {
    > div {
      font-size: 18px;
      margin-bottom: 7px;
      &.provider_applicant_id,
      &.apiKey,
      &.uid {
        cursor: pointer;
      }
      span.greyText {
        color: #7e7e7e;
        margin-right: 5px;
      }
    }
  }

  .accountActionBtn {
    width: 100%;
    display: block;
    padding-left: 4px;
    padding-right: 4px;
  }

  .limitsHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 600;
  }
}
</style>
